/**
 * The surface an admin CRM table sits in: heading, how to use it, then search.
 *
 * The speakers roster established this order and it is the right one — the panel
 * names itself, explains its own affordances (right-click, the Actions button),
 * and only then offers the search. The members table had the same three parts in
 * a different arrangement, with the search floating above the panel entirely and
 * no room for a description, so the two tables read as different products.
 *
 * The count sits beside the search rather than in the heading because it
 * describes the result of searching: "2 of 11 profiles" answers a question the
 * administrator only has once they have typed something.
 *
 * Children are the table itself. This owns everything around it and nothing
 * inside it, so each table keeps its own columns, sorting and row rendering.
 */
import type { LucideIcon } from "lucide-react";
import { Search } from "lucide-react";
import type { ReactNode } from "react";

const surface = {
  background: "rgba(255,255,255,.028)",
  border: "1px solid rgba(255,255,255,.09)",
  borderRadius: ".9rem",
} as const;

export default function CrmTablePanel({
  title,
  description,
  icon: Icon,
  iconColor = "#E91E8C",
  search,
  onSearchChange,
  searchPlaceholder,
  searchLabel,
  /** How many rows are showing after filtering and searching. */
  shownCount,
  /** How many exist in total, before either. */
  totalCount,
  /** What a row is, singular — "profile", "member". Pluralised with a trailing s. */
  noun,
  /** Shown instead of the table and search when there is nothing at all yet. */
  emptyState,
  children,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
  iconColor?: string;
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  searchLabel: string;
  shownCount: number;
  totalCount: number;
  noun: string;
  emptyState?: ReactNode;
  children: ReactNode;
}) {
  const showEmptyState = totalCount === 0 && emptyState;

  return (
    <section className="crm-table-panel" style={{ ...surface, overflow: "hidden" }} aria-labelledby={`${title.replace(/\s+/g, "-").toLowerCase()}-heading`}>
      <div className="crm-table-panel__header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", padding: "1rem 1.1rem", borderBottom: "1px solid rgba(255,255,255,.07)" }}>
        <div>
          <h2
            id={`${title.replace(/\s+/g, "-").toLowerCase()}-heading`}
            style={{ margin: 0, color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".96rem", fontWeight: 700 }}
          >
            {title}
          </h2>
          <p style={{ margin: ".2rem 0 0", color: "#657289", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            {description}
          </p>
        </div>
        <Icon size={20} color={iconColor} aria-hidden="true" style={{ flex: "0 0 auto" }} />
      </div>

      {showEmptyState ? emptyState : (
        <>
          <div className="crm-table-panel__toolbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".75rem", flexWrap: "wrap", padding: ".85rem 1.1rem" }}>
            <div style={{ position: "relative", flex: "1 1 260px", maxWidth: 460 }}>
              <Search size={15} aria-hidden="true" style={{ position: "absolute", left: ".75rem", top: "50%", transform: "translateY(-50%)", color: "#64748B" }} />
              <input
                aria-label={searchLabel}
                value={search}
                onChange={event => onSearchChange(event.target.value)}
                placeholder={searchPlaceholder}
                style={{ width: "100%", minHeight: 40, boxSizing: "border-box", padding: ".62rem .75rem .62rem 2.25rem", borderRadius: ".55rem", border: "1px solid rgba(255,255,255,.1)", background: "rgba(8,12,24,.72)", color: "white", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}
              />
            </div>
            <p style={{ margin: 0, color: "#657289", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
              {shownCount} of {totalCount} {totalCount === 1 ? noun : `${noun}s`}
            </p>
          </div>
          {children}
        </>
      )}
    </section>
  );
}
